import React from 'react';
import { Link } from 'react-router-dom';

// Self-hosted copy of the Privacy Policy. It used to live on an external
// generator's domain, which meant the one legal document users are most likely
// to check sat on a site that isn't ours — and looked nothing like the rest of
// the product. The text is unchanged apart from the contact address, which now
// points at support@ like every other contact route in the app.
//
// Deliberately styled from the same `s` object as Terms.jsx so the three
// standalone legal/help pages (Support, Terms, Privacy) read as one set.
const Sec = ({ h, children }) => (
  <section style={s.sec}>
    <h2 style={s.h2}>{h}</h2>
    {children}
  </section>
);

const Sub = ({ h, children }) => (
  <>
    <h3 style={s.h3}>{h}</h3>
    {children}
  </>
);

const Li = ({ children }) => <li style={s.li}>{children}</li>;

// Definition rows: the term carries the emphasis, the gloss stays body copy.
const Def = ({ term, children }) => (
  <p style={s.p}>
    <strong style={s.term}>{term}</strong> {children}
  </p>
);

const SUPPORT_EMAIL = 'support@mycarcollection.app';

const Privacy = () => (
  <div style={s.page}>
    <div style={s.container}>
      <nav style={s.nav}>
        <Link to="/" style={s.brand}>MyCarCollection</Link>
      </nav>

      <header style={s.header}>
        <p style={s.muted}>Last updated: 13 August 2026</p>
        <h1 style={s.h1}>Privacy Policy</h1>
      </header>

      <p style={s.p}>
        This Privacy Policy describes Our policies and procedures on the collection, use and
        disclosure of Your information when You use the Service and tells You about Your privacy
        rights and how the law protects You.
      </p>
      <p style={s.p}>
        We use Your Personal Data to provide and improve the Service. By using the Service, You
        agree to the collection and use of information in accordance with this Privacy Policy.
        This Privacy Policy has been created with the help of the TermsFeed Privacy Policy
        Generator.
      </p>

      <Sec h="Interpretation and Definitions">
        <Sub h="Interpretation">
          <p style={s.p}>
            The words whose initial letters are capitalized have meanings defined under the
            following conditions. The following definitions shall have the same meaning regardless
            of whether they appear in singular or in plural.
          </p>
        </Sub>

        <Sub h="Definitions">
          <p style={s.p}>For the purposes of this Privacy Policy:</p>
          <Def term="Account">
            means a unique account created for You to access our Service or parts of our Service.
          </Def>
          <Def term="Affiliate">
            means an entity that controls, is controlled by, or is under common control with a
            party, where “control” means ownership of 50% or more of the shares, equity interest or
            other securities entitled to vote for election of directors or other managing authority.
          </Def>
          <Def term="Application">
            refers to My Car Collection, the software program provided by the Company.
          </Def>
          <Def term="Company">
            (referred to as either “the Company”, “We”, “Us” or “Our” in this Privacy Policy) refers
            to My Car Collection.
          </Def>
          <Def term="Country">refers to: New South Wales, Australia</Def>
          <Def term="Device">
            means any device that can access the Service such as a computer, a cell phone or a
            digital tablet.
          </Def>
          <Def term="Personal Data">
            (or “Personal Information”) is any information that relates to an identified or
            identifiable individual. We use “Personal Data” and “Personal Information”
            interchangeably unless a law uses a specific term.
          </Def>
          <Def term="Service">refers to the Application.</Def>
          <Def term="Service Provider">
            means any natural or legal person who processes the data on behalf of the Company. It
            refers to third-party companies or individuals employed by the Company to facilitate the
            Service, to provide the Service on behalf of the Company, to perform services related to
            the Service or to assist the Company in analyzing how the Service is used.
          </Def>
          <Def term="Usage Data">
            refers to data collected automatically, either generated by the use of the Service or
            from the Service infrastructure itself (for example, the duration of a page visit).
          </Def>
          <Def term="You">
            means the individual accessing or using the Service, or the company, or other legal
            entity on behalf of which such individual is accessing or using the Service, as
            applicable.
          </Def>
        </Sub>
      </Sec>

      <Sec h="Collecting and Using Your Personal Data">
        <Sub h="Types of Data Collected">
          <p style={s.p}>
            <strong style={s.term}>Personal Data.</strong> While using Our Service, We may ask You
            to provide Us with certain personally identifiable information that can be used to
            contact or identify You. Personally identifiable information may include, but is not
            limited to:
          </p>
          <ul style={s.ul}>
            <Li>Email address</Li>
            <Li>First name and last name</Li>
            <Li>Address, State, Province, ZIP/Postal code, City</Li>
          </ul>

          <p style={s.p}>
            <strong style={s.term}>Usage Data.</strong> Usage Data is collected automatically when
            using the Service. Usage Data may include information such as Your Device’s Internet
            Protocol address (e.g. IP address), browser type, browser version, the pages of our
            Service that You visit, the time and date of Your visit, the time spent on those pages,
            unique device identifiers and other diagnostic data.
          </p>
          <p style={s.p}>
            When You access the Service by or through a mobile device, We may collect certain
            information automatically, including, but not limited to, the type of mobile device You
            use, Your mobile device’s unique ID, the IP address of Your mobile device, Your mobile
            operating system, the type of mobile Internet browser You use, unique device identifiers
            and other diagnostic data.
          </p>
          <p style={s.p}>
            We may also collect information that Your browser sends whenever You visit Our Service
            or when You access the Service by or through a mobile device.
          </p>

          <p style={s.p}>
            <strong style={s.term}>Information Collected while Using the Application.</strong> While
            using Our Application, in order to provide features of Our Application, We may collect,
            with Your prior permission:
          </p>
          <ul style={s.ul}>
            <Li>Information regarding your location</Li>
            <Li>Pictures and other information from your Device’s camera and photo library</Li>
          </ul>
          <p style={s.p}>
            We use this information to provide features of Our Service, to improve and customize Our
            Service. The information may be uploaded to the Company’s servers and/or a Service
            Provider’s server or it may be simply stored on Your device. You can enable or disable
            access to this information at any time, through Your Device settings.
          </p>
          <p style={s.p}>
            <strong style={s.term}>How We use Your location, specifically.</strong> The Application
            asks for location permission so that features such as events, routes and landmarks can
            show You what is near You and sort it by distance. Those calculations happen on Your
            device.
          </p>
          <p style={s.p}>
            In addition, We store an <strong style={s.term}>approximate</strong> location against
            Your account so We can tell You when a major car event is happening in Your area. Before
            it is stored, the position is rounded to roughly one kilometre — We keep a general area,
            such as a suburb, not Your precise position, and We do not keep a history of where You
            have been. It is recorded no more than once a day, only from a location reading the
            Application has already obtained with Your permission for the features described above,
            and only while You are using the Application. We do not collect Your location in the
            background, and We do not use it for advertising or share it with third parties.
          </p>
          <p style={s.p}>
            You can stop this at any time: turn off <em>Big events near you</em> under Settings →
            Notifications in the Application to stop receiving these notifications, or revoke
            location permission in Your Device settings to stop the approximate location being
            recorded at all. Deleting Your Account deletes it along with the rest of Your data.
          </p>

          <p style={s.p}>
            <strong style={s.term}>Subscription information.</strong> If You take out an optional
            <em> Support the App</em> subscription, the purchase itself is made with Apple or
            Google, not with Us. <strong style={s.term}>We never receive or store Your payment
            card details, billing address, or Apple ID or Google account credentials.</strong> The
            store handles the payment and tells Our subscription provider, RevenueCat, the outcome.
          </p>
          <p style={s.p}>
            What We store against Your account is limited to what We need to know that You are a
            supporter and at what level: which tier You are on, whether it is currently active,
            whether it is set to renew, when the current period ends, the product identifier, which
            store the purchase came from, and the date You first became a supporter. We also keep
            the purchase identifier the store uses, so We can look up Your subscription if You
            contact Us about it.
          </p>
          <p style={s.p}>
            Being a supporter is visible to others. Your username, profile picture, the date You
            became a supporter, and a chip whose colour reflects Your tier appear on the Supporters
            Wall in the mobile app, which any signed-in user of the app can read. The Wall is not
            shown on the MyCarCollection website and is not published on the open web, and the
            amount You pay is never shown. If Your subscription lapses You are
            removed from the Wall. Deleting Your Account deletes this information along with the
            rest of Your data — note that deleting Your Account does not cancel the subscription
            itself, which You must cancel through Apple or Google.
          </p>
        </Sub>

        <Sub h="Use of Your Personal Data">
          <p style={s.p}>The Company may use Personal Data for the following purposes:</p>
          <ul style={s.ul}>
            <Li>
              <strong style={s.term}>To provide and maintain our Service</strong>, including to
              monitor the usage of our Service.
            </Li>
            <Li>
              <strong style={s.term}>To manage Your Account:</strong> to manage Your registration as
              a user of the Service. The Personal Data You provide can give You access to different
              functionalities of the Service that are available to You as a registered user.
            </Li>
            <Li>
              <strong style={s.term}>For the performance of a contract:</strong> the development,
              compliance and undertaking of the purchase contract for the products, items or
              services You have purchased or of any other contract with Us through the Service.
            </Li>
            <Li>
              <strong style={s.term}>To contact You:</strong> To contact You by email, telephone
              calls, SMS, or other equivalent forms of electronic communication, such as a mobile
              application’s push notifications regarding updates or informative communications
              related to the functionalities, products or contracted services, including the
              security updates, when necessary or reasonable for their implementation.
            </Li>
            <Li>
              To provide You with news, special offers, and general information about other goods,
              services and events which We offer that are similar to those that you have already
              purchased or inquired about unless You have opted not to receive such information.
            </Li>
            <Li>
              <strong style={s.term}>To manage Your requests:</strong> To attend and manage Your
              requests to Us.
            </Li>
            <Li>
              <strong style={s.term}>For business transfers:</strong> We may use Your Personal Data
              to evaluate or conduct a merger, divestiture, restructuring, reorganization,
              dissolution, or other sale or transfer of some or all of Our assets, whether as a
              going concern or as part of bankruptcy, liquidation, or similar proceeding, in which
              Personal Data held by Us about our Service users is among the assets transferred.
            </Li>
            <Li>
              <strong style={s.term}>For other purposes:</strong> We may use Your information for
              other purposes, such as data analysis, identifying usage trends, determining the
              effectiveness of our promotional campaigns and to evaluate and improve our Service,
              products, services, marketing and your experience.
            </Li>
          </ul>

          <p style={s.p}>We may share Your Personal Data in the following situations:</p>
          <ul style={s.ul}>
            <Li>
              <strong style={s.term}>With Service Providers:</strong> We may share Your Personal
              Data with Service Providers to monitor and analyze the use of our Service, to contact
              You.
            </Li>
            <Li>
              <strong style={s.term}>For business transfers:</strong> We may share or transfer Your
              Personal Data in connection with, or during negotiations of, any merger, sale of
              Company assets, financing, or acquisition of all or a portion of Our business to
              another company.
            </Li>
            <Li>
              <strong style={s.term}>With Affiliates:</strong> We may share Your Personal Data with
              Our affiliates, in which case we will require those affiliates to honor this Privacy
              Policy. Affiliates include Our parent company and any other subsidiaries, joint
              venture partners or other companies that We control or that are under common control
              with Us.
            </Li>
            <Li>
              <strong style={s.term}>With business partners:</strong> We may share Your Personal
              Data with Our business partners to offer You certain products, services or promotions.
            </Li>
            <Li>
              <strong style={s.term}>With other users:</strong> If Our Service offers public areas,
              when You share Personal Data or otherwise interact in the public areas with other
              users, such information may be viewed by all users and may be publicly distributed
              outside.
            </Li>
            <Li>
              <strong style={s.term}>With Your consent:</strong> We may disclose Your Personal Data
              for any other purpose with Your consent.
            </Li>
          </ul>
        </Sub>

        <Sub h="Retention of Your Personal Data">
          <p style={s.p}>
            The Company will retain Your Personal Data only for as long as is necessary for the
            purposes set out in this Privacy Policy. We will retain and use Your Personal Data to
            the extent necessary to comply with our legal obligations (for example, if We are
            required to retain Your data to comply with applicable laws), resolve disputes, and
            enforce our legal agreements and policies.
          </p>
          <p style={s.p}>
            Where possible, We apply shorter retention periods and/or reduce identifiability by
            deleting, aggregating, or anonymizing data. Unless otherwise stated, the retention
            periods below are maximum periods (“up to”) and We may delete or anonymize data sooner
            when it is no longer needed for the relevant purpose. We apply different retention
            periods to different categories of Personal Data based on the purpose of processing and
            legal obligations:
          </p>
          <p style={s.p}><strong style={s.term}>Account Information</strong></p>
          <ul style={s.ul}>
            <Li>
              User Accounts: retained for the duration of your account relationship plus up to 24
              months after account closure to handle any post-termination issues or resolve
              disputes.
            </Li>
          </ul>
          <p style={s.p}><strong style={s.term}>Customer Support Data</strong></p>
          <ul style={s.ul}>
            <Li>
              Support tickets and correspondence: up to 24 months from the date of ticket closure to
              resolve follow-up inquiries, track service quality, and defend against potential legal
              claims.
            </Li>
            <Li>Chat transcripts: up to 24 months for quality assurance and staff training purposes.</Li>
          </ul>
          <p style={s.p}><strong style={s.term}>Usage Data</strong></p>
          <ul style={s.ul}>
            <Li>
              Application usage statistics: up to 24 months to understand feature adoption and
              service improvements.
            </Li>
            <Li>
              Server logs (IP addresses, access times): up to 24 months for security monitoring and
              troubleshooting purposes.
            </Li>
          </ul>
          <p style={s.p}>
            Usage Data is retained in accordance with the retention periods described above, and may
            be retained longer only where necessary for security, fraud prevention, or legal
            compliance.
          </p>
          <p style={s.p}>
            We may retain Personal Data beyond the periods stated above for different reasons:
          </p>
          <ul style={s.ul}>
            <Li>
              <strong style={s.term}>Legal obligation:</strong> We are required by law to retain
              specific data (e.g., financial records for tax authorities).
            </Li>
            <Li>
              <strong style={s.term}>Legal claims:</strong> Data is necessary to establish,
              exercise, or defend legal claims.
            </Li>
            <Li>
              <strong style={s.term}>Your explicit request:</strong> You ask Us to retain specific
              information.
            </Li>
            <Li>
              <strong style={s.term}>Technical limitations:</strong> Data exists in backup systems
              that are scheduled for routine deletion.
            </Li>
          </ul>
          <p style={s.p}>
            You may request information about how long We will retain Your Personal Data by
            contacting Us.
          </p>
          <p style={s.p}>
            When retention periods expire, We securely delete or anonymize Personal Data according
            to the following procedures:
          </p>
          <ul style={s.ul}>
            <Li>
              <strong style={s.term}>Deletion:</strong> Personal Data is removed from Our systems
              and no longer actively processed.
            </Li>
            <Li>
              <strong style={s.term}>Backup retention:</strong> Residual copies may remain in
              encrypted backups for a limited period consistent with our backup retention schedule
              and are not restored except where necessary for security, disaster recovery, or legal
              compliance.
            </Li>
            <Li>
              <strong style={s.term}>Anonymization:</strong> In some cases, We convert Personal Data
              into anonymous statistical data that cannot be linked back to You. This anonymized
              data may be retained indefinitely for research and analytics.
            </Li>
          </ul>
        </Sub>

        <Sub h="Transfer of Your Personal Data">
          <p style={s.p}>
            Your information, including Personal Data, is processed at the Company’s operating
            offices and in any other places where the parties involved in the processing are
            located. It means that this information may be transferred to — and maintained on —
            computers located outside of Your state, province, country or other governmental
            jurisdiction where the data protection laws may differ from those from Your
            jurisdiction.
          </p>
          <p style={s.p}>
            Where required by applicable law, We will ensure that international transfers of Your
            Personal Data are subject to appropriate safeguards and supplementary measures where
            appropriate. The Company will take all steps reasonably necessary to ensure that Your
            data is treated securely and in accordance with this Privacy Policy and no transfer of
            Your Personal Data will take place to an organization or a country unless there are
            adequate controls in place including the security of Your data and other personal
            information.
          </p>
        </Sub>

        <Sub h="Delete Your Personal Data">
          <p style={s.p}>
            You have the right to delete or request that We assist in deleting the Personal Data
            that We have collected about You. Our Service may give You the ability to delete certain
            information about You from within the Service.
          </p>
          <p style={s.p}>
            You may update, amend, or delete Your information at any time by signing in to Your
            Account, if you have one, and visiting the account settings section that allows you to
            manage Your personal information. You may also contact Us to request access to, correct,
            or delete any Personal Data that You have provided to Us.
          </p>
          <p style={s.p}>
            Please note, however, that We may need to retain certain information when we have a
            legal obligation or lawful basis to do so.
          </p>
        </Sub>

        <Sub h="Disclosure of Your Personal Data">
          <p style={s.p}>
            <strong style={s.term}>Business Transactions.</strong> If the Company is involved in a
            merger, acquisition or asset sale, Your Personal Data may be transferred. We will
            provide notice before Your Personal Data is transferred and becomes subject to a
            different Privacy Policy.
          </p>
          <p style={s.p}>
            <strong style={s.term}>Law enforcement.</strong> Under certain circumstances, the
            Company may be required to disclose Your Personal Data if required to do so by law or in
            response to valid requests by public authorities (e.g. a court or a government agency).
          </p>
          <p style={s.p}>
            <strong style={s.term}>Other legal requirements.</strong> The Company may disclose Your
            Personal Data in the good faith belief that such action is necessary to:
          </p>
          <ul style={s.ul}>
            <Li>Comply with a legal obligation</Li>
            <Li>Protect and defend the rights or property of the Company</Li>
            <Li>Prevent or investigate possible wrongdoing in connection with the Service</Li>
            <Li>Protect the personal safety of Users of the Service or the public</Li>
            <Li>Protect against legal liability</Li>
          </ul>
        </Sub>

        <Sub h="Security of Your Personal Data">
          <p style={s.p}>
            The security of Your Personal Data is important to Us, but remember that no method of
            transmission over the Internet, or method of electronic storage is 100% secure. While We
            strive to use commercially reasonable means to protect Your Personal Data, We cannot
            guarantee its absolute security.
          </p>
        </Sub>
      </Sec>

      <Sec h="Detailed Information on the Processing of Your Personal Data">
        <p style={s.p}>
          The Service Providers We use may have access to Your Personal Data. These third-party
          vendors collect, store, use, process and transfer information about Your activity on Our
          Service in accordance with their Privacy Policies.
        </p>

        <Sub h="Usage, Performance and Miscellaneous">
          <p style={s.p}>We may use third-party Service Providers to maintain and improve our Service.</p>
          <p style={s.p}>
            <strong style={s.term}>Google Places.</strong> Google Places is a service that returns
            information about places using HTTP requests. It is operated by Google. Google Places
            service may collect information from You and from Your Device for security purposes. The
            information gathered by Google Places is held in accordance with the Privacy Policy of
            Google:{' '}
            <a
              href="https://www.google.com/intl/en/policies/privacy/"
              style={s.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.google.com/intl/en/policies/privacy/
            </a>
          </p>
          <p style={s.p}>
            <strong style={s.term}>Mapbox.</strong> Their Privacy Policy can be viewed at{' '}
            <a
              href="https://www.mapbox.com/legal/privacy"
              style={s.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.mapbox.com/legal/privacy
            </a>
          </p>
        </Sub>

        <Sub h="Payments and Subscriptions">
          <p style={s.p}>
            Optional <em>Support the App</em> subscriptions are sold and processed by the app store
            You bought them from — Apple’s App Store or Google Play. They take the payment and hold
            Your payment details; We never see them. Their handling of Your information is governed
            by their own policies:{' '}
            <a
              href="https://www.apple.com/legal/privacy/"
              style={s.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.apple.com/legal/privacy/
            </a>{' '}
            and{' '}
            <a
              href="https://policies.google.com/privacy"
              style={s.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              https://policies.google.com/privacy
            </a>
          </p>
          <p style={s.p}>
            <strong style={s.term}>RevenueCat.</strong> We use RevenueCat to manage subscription
            status across the stores and to tell Our servers when a subscription starts, renews,
            changes tier, or ends. RevenueCat receives an identifier for Your account and the
            purchase details reported by the store; it does not receive Your payment card details.
            Their Privacy Policy can be viewed at{' '}
            <a
              href="https://www.revenuecat.com/privacy"
              style={s.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.revenuecat.com/privacy
            </a>
          </p>
        </Sub>
      </Sec>

      <Sec h="Children’s Privacy">
        <p style={s.p}>
          Our Service does not address anyone under the age of 16. We do not knowingly collect
          personally identifiable information from anyone under the age of 16. If You are a parent
          or guardian and You are aware that Your child has provided Us with Personal Data, please
          contact Us. If We become aware that We have collected Personal Data from anyone under the
          age of 16 without verification of parental consent, We take steps to remove that
          information from Our servers.
        </p>
        <p style={s.p}>
          If We need to rely on consent as a legal basis for processing Your information and Your
          country requires consent from a parent, We may require Your parent’s consent before We
          collect and use that information.
        </p>
      </Sec>

      <Sec h="Links to Other Websites">
        <p style={s.p}>
          Our Service may contain links to other websites that are not operated by Us. If You click
          on a third party link, You will be directed to that third party’s site. We strongly advise
          You to review the Privacy Policy of every site You visit.
        </p>
        <p style={s.p}>
          We have no control over and assume no responsibility for the content, privacy policies or
          practices of any third party sites or services.
        </p>
      </Sec>

      <Sec h="Changes to this Privacy Policy">
        <p style={s.p}>
          We may update Our Privacy Policy from time to time. We will notify You of any changes by
          posting the new Privacy Policy on this page.
        </p>
        <p style={s.p}>
          We will let You know via email and/or a prominent notice on Our Service, prior to the
          change becoming effective and update the “Last updated” date at the top of this Privacy
          Policy.
        </p>
        <p style={s.p}>
          You are advised to review this Privacy Policy periodically for any changes. Changes to
          this Privacy Policy are effective when they are posted on this page.
        </p>
      </Sec>

      <Sec h="Contact Us">
        <p style={s.p}>
          If you have any questions about this Privacy Policy, You can contact us by email at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={s.link}>{SUPPORT_EMAIL}</a>, or through our{' '}
          <Link to="/support" style={s.link}>Support page</Link>.
        </p>
      </Sec>

      <p style={s.footer}>
        See also our <Link to="/terms" style={s.link}>Terms of Use</Link>.
      </p>
    </div>
  </div>
);

const s = {
  page: { minHeight: '100vh', color: '#e6e9f0', paddingBottom: 80 },
  container: { maxWidth: 760, margin: '0 auto', padding: '0 24px' },
  nav: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '24px 0', marginBottom: 24,
  },
  brand: {
    color: '#fff', fontSize: '1.05rem', fontWeight: 800, letterSpacing: -0.4,
    textDecoration: 'none',
  },
  header: { padding: '24px 0 8px' },
  muted: { color: '#5a657a', fontSize: '0.8rem', marginBottom: 8 },
  h1: { fontSize: '1.9rem', fontWeight: 800, letterSpacing: -0.6, lineHeight: 1.15, marginBottom: 8 },
  sec: { marginTop: 28 },
  h2: { fontSize: '1rem', fontWeight: 700, marginBottom: 8, color: '#fff' },
  h3: { fontSize: '0.92rem', fontWeight: 700, margin: '18px 0 6px', color: '#fff' },
  p: { color: '#c8cfe0', fontSize: '0.92rem', lineHeight: 1.65, marginBottom: 10 },
  ul: { paddingLeft: 22, margin: '4px 0 10px' },
  li: { color: '#c8cfe0', fontSize: '0.92rem', lineHeight: 1.6, marginBottom: 4 },
  term: { color: '#fff', fontWeight: 650 },
  link: { color: '#4a9eff', textDecoration: 'underline' },
  footer: {
    marginTop: 36, fontStyle: 'italic',
    color: '#8892b0', fontSize: '0.85rem', lineHeight: 1.6,
  },
};

export default Privacy;
